import { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { cardFormSchema, upiFormSchema, netBankingSchema, walletSchema } from '../utils/validation';
import { PAYMENT_METHODS } from '../types/index';
import { processPayment } from '../services/paymentApi';
import { toast } from 'sonner';

/**
 * Returns the correct zod resolver for each payment method.
 * @param {string} method
 */
function getResolver(method) {
    switch (method) {
        case PAYMENT_METHODS.CARD: return zodResolver(cardFormSchema);
        case PAYMENT_METHODS.UPI: return zodResolver(upiFormSchema);
        case PAYMENT_METHODS.NET_BANKING: return zodResolver(netBankingSchema);
        case PAYMENT_METHODS.WALLET: return zodResolver(walletSchema);
        default: return undefined;
    }
}

/**
 * Manages payment method selection, form state, and payment submission.
 *
 * @param {string} token - Auth token from useAuth()
 * @param {import('../hooks/usePaymentState').usePaymentState} paymentState
 * @param {Object} [orderData] - Optional order context passed to API
 * @returns {{
 *   selectedMethod: string,
 *   setSelectedMethod: (m: string) => void,
 *   form: import('react-hook-form').UseFormReturn,
 *   onSubmit: (formData: Object) => Promise<void>,
 * }}
 */
export function usePaymentForm(token, paymentState, orderData = {}) {
    const [selectedMethod, setSelectedMethod] = useState(PAYMENT_METHODS.CARD);

    const form = useForm({
        resolver: getResolver(selectedMethod),
        defaultValues: {
            cardholderName: '',
            cardNumber: '',
            expiryDate: '',
            cvv: '',
            saveCard: false,
            hasBillingAddress: false,
            billingAddress: {
                line1: '', line2: '', city: '', state: '', zip: '', country: '',
            },
            upiId: '',
            bankId: '',
            walletId: '',
        },
        mode: 'onTouched',
    });

    // Re-register resolver when method changes
    const handleMethodChange = useCallback((method) => {
        setSelectedMethod(method);
        form.clearErrors();
    }, [form]);

    const onSubmit = useCallback(
        async (formData) => {
            paymentState.startProcessing();
            try {
                await processPayment(
                    {
                        method: selectedMethod,
                        formData,
                        ...orderData,
                    },
                    token
                );
                paymentState.setSuccess();
                toast.success('Payment successful!');
            } catch (err) {
                const msg = err?.response?.data?.detail || err?.message || 'Payment failed. Please try again.';
                paymentState.setFailed(msg);
                toast.error(msg);
            }
        },
        [token, selectedMethod, paymentState, orderData]
    );

    return {
        selectedMethod,
        setSelectedMethod: handleMethodChange,
        form,
        onSubmit,
    };
}
